import { useEffect } from 'react';
import { itemRender, showTotal } from 'shared/UI/UIKit/Pagination/Pagination.utils';
import { getDefaultTablePagination } from 'shared/UI/UIKit/Table';
import type { SorterResult } from 'antd/lib/table/interface';
import type { AppFilter } from '../../../lib';
import type { AppTableItem, AppTablePagination, AppTableSorter } from '../../types';
import type { TableChangeServiceOptions, TableChangeService } from './types';
import { getChangeEvent } from '../../../lib';

export const useTableChange = <TRecord extends AppTableItem>({
  onChange,
  sorters,
  filters,
  pagination,
  paginationVisible,
}: TableChangeServiceOptions): TableChangeService<TRecord> => {
  useEffect(() => {
    const tableState = {
      sorters,
      filters,
      pagination,
    };
    const event = getChangeEvent(tableState);
    onChange?.(tableState, event, 'init');
  }, []);

  const onSorterChange = (nextSorter: SorterResult<TRecord> | SorterResult<TRecord>[]) => {
    const getSorters = (): AppTableSorter[] => {
      const nextSorters = Array.isArray(nextSorter) ? nextSorter : [nextSorter];

      return sorters.map((resultSorter) => {
        const matchedIndex = nextSorters.findIndex(
          (sorter) => sorter.columnKey === resultSorter.key,
        );
        const matchedSorter = matchedIndex >= 0 ? nextSorters[matchedIndex] : undefined;

        return {
          ...resultSorter,
          order: matchedSorter?.order ?? undefined,
          weight: matchedSorter?.order != null ? matchedIndex : resultSorter.weight,
        };
      });
    };

    const tableState = {
      sorters: getSorters(),
      filters,
      pagination,
    };
    const event = getChangeEvent(tableState);
    onChange?.(tableState, event, 'sort');
  };

  const onPaginationChange = (nextPagination: AppTablePagination) => {
    const updatedPagination = {
      ...nextPagination,
      itemRender: itemRender,
      showTotal: showTotal,
    };
    const tableState = {
      sorters,
      filters,
      pagination: paginationVisible ? updatedPagination : pagination,
    };
    const event = getChangeEvent(tableState);
    onChange?.(tableState, event, 'paginate');
  };

  const onFilterChange = (nextFilter: AppFilter) => {
    const nextFilters = [...filters];
    const index = nextFilters.findIndex((filterItem) => filterItem.key === nextFilter.key);
    if (index !== -1) {
      nextFilters.splice(index, 1, nextFilter);
    } else {
      nextFilters.push(nextFilter);
    }

    const tableState = {
      sorters,
      filters: nextFilters,
      pagination: { ...pagination, current: getDefaultTablePagination().current },
    };
    const event = getChangeEvent(tableState);
    onChange?.(tableState, event, 'filter');
  };

  const onTableChange: TableChangeService<TRecord>['onTableChange'] = (
    nextPagination,
    nextFilters,
    nextSorter,
    extra,
  ) => {
    if (extra?.action === 'sort') {
      onSorterChange?.(nextSorter);
    }
    if (extra?.action === 'paginate') {
      onPaginationChange?.(nextPagination);
    }
  };

  return {
    onTableChange,
    onFilterChange,
  };
};
